'use client'
// InitializedMDXEditor.tsx
import 'prismjs'
import 'prismjs/components/prism-markdown'
import type { ForwardedRef } from 'react'
import { useEffect, useRef } from 'react'
import {
    headingsPlugin,
    listsPlugin,
    linkPlugin,
    linkDialogPlugin,
    quotePlugin,
    thematicBreakPlugin,
    markdownShortcutPlugin,
    toolbarPlugin,
    MDXEditor,
    type MDXEditorMethods,
    type MDXEditorProps,
    UndoRedo,
    BoldItalicUnderlineToggles,
    BlockTypeSelect,
    InsertTable,
    ListsToggle,
    CreateLink,
    StrikeThroughSupSubToggles,
    tablePlugin,
} from '@mdxeditor/editor'
import { isExternal, openExternal } from '@/utils/externalLinks'


// Only import this to the next file
export default function InitializedMDXEditor({
    editorRef,
    ...props
}: { editorRef: ForwardedRef<MDXEditorMethods> | null } & MDXEditorProps) {
    const editorContainerRef = useRef<HTMLDivElement>(null);

    // MDX Editor рендерит markdown-контент в Shadow DOM,
    // поэтому глобальный обработчик кликов не действует на ссылки внутри него.
    // Перехватываем клики на shadow root.
    useEffect(() => {
        const container = editorContainerRef.current;
        if (!container) return;

        const handleClick = (e: Event) => {
            const mouseEvent = e as MouseEvent;
            const anchor = (mouseEvent.target as Element).closest('a');
            if (!anchor || !anchor.href) return;

            const href = anchor.getAttribute('href');
            if (!href || href.startsWith('#') || href.startsWith('data:')) return;
            if (mouseEvent.ctrlKey || mouseEvent.metaKey || mouseEvent.shiftKey || mouseEvent.altKey) return;

            if (isExternal(href)) {
                e.preventDefault();
                e.stopPropagation();
                openExternal(href);
            }
        };

        // Находим все shadow roots в контейнере (включая вложенные)
        const attachToShadowRoots = (root: ShadowRoot) => {
            root.addEventListener('click', handleClick as EventListener, true);
            // Также ищем вложенные shadow roots
            root.querySelectorAll('*').forEach(el => {
                if (el.shadowRoot) {
                    attachToShadowRoots(el.shadowRoot);
                }
            });
        };

        // MDX Editor создаёт shadow root на самом элементе или дочерних
        // Ищем shadow root на контейнере и его потомках
        if (container.shadowRoot) {
            attachToShadowRoots(container.shadowRoot);
        }
        container.querySelectorAll('*').forEach(el => {
            if (el.shadowRoot) {
                attachToShadowRoots(el.shadowRoot);
            }
        });

        // Также слушаем MutationObserver на случай появления новых shadow roots
        const observer = new MutationObserver(() => {
            container.querySelectorAll('*').forEach(el => {
                if (el.shadowRoot && !el.shadowRoot.contains(container)) {
                    attachToShadowRoots(el.shadowRoot);
                }
            });
        });
        observer.observe(container, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <div ref={editorContainerRef}>
            <MDXEditor
                plugins={[
                    // Example Plugin Usage
                    headingsPlugin(),
                    listsPlugin(),
                    quotePlugin(),
                    tablePlugin(),
                    linkPlugin(),
                    linkDialogPlugin(),
                    thematicBreakPlugin(),
                    markdownShortcutPlugin(),
                    toolbarPlugin({
                        toolbarClassName: 'my-classname',
                        toolbarContents: () => (
                            <>
                                <UndoRedo />
                                <BlockTypeSelect />
                                <InsertTable />
                                <ListsToggle />
                                <BoldItalicUnderlineToggles />
                                <StrikeThroughSupSubToggles />
                                <CreateLink />
                            </>
                        )
                    }),
                ]}
                {...props}

                ref={editorRef}
            />
        </div>
    )
}

// TODO: отображать просто markdown в режиме для чтения. при этом, если маркаун меняется, то нужно использовать ref чтобы его обновить в компоненте